import { TRANSACTION_TTL } from '../utils/constants'
import { SQLWorkerEnvelope } from './SQLBuilder'
import { WorkerRequest } from './SQLiteFactory'
import { WorkerRequestQueue } from './WorkerRequestQueue'
import { createSharedServicePort } from './sql.sharedservice'

// this should only ever be called from within the provider handler via a proxy
// do not export this function
function providerhandler_enqueueMessage(req: WorkerRequest) {
    if (req.type.includes('transaction') && !req.transactionCorrelationId) {
        throw new Error(
            'Transactions must be handled by SQLiteTransactionManager. Are you using txManager in the `executeTransaction` callback?'
        )
    }

    const promise = new Promise<any[]>((resolve, reject) => {
        const timeoutId = setTimeout(
            () => {
                reject(JSON.stringify({ error: 'timeout', payload: req }))
            },
            (req.timeoutSeconds || TRANSACTION_TTL) * 1000
        )

        WorkerRequestQueue.getInstance().enqueue({
            ...req,
            timeoutId,
            resolve,
            reject,
        })
    })

    return promise
}

// This is the interface that allows any tab to communicate with the provider via the shared worker.
// **All** communication between tabs will be initiated here.
// The "provider" (i.e. the tab that has the database open) will ultimately be the one that
// receives this request and then passes the request to the database worker.

// This handler is dormant until its tab is promoted to provider, at which point the shared worker
// will wrap this handler in a proxy that will forward all requests to the provider. Any tab can
// call e.g. `SQLiteFactory.getInstance().proxy["initializeSql"]` and have the provider handle the
// request.
const providerSQLHandlers = {
    async createSqlDb() {
        return await providerhandler_enqueueMessage({
            type: 'create',
            timeoutSeconds: 15,
        })
    },
    async initializeSql() {
        return await providerhandler_enqueueMessage({
            type: 'initialize',
            timeoutSeconds: 15,
        })
    },
    async dropDB() {
        return await providerhandler_enqueueMessage({
            type: 'drop',
            timeoutSeconds: 15,
        })
    },
    async sendSqlEnvelope(payload: SQLWorkerEnvelope, singleRow: boolean) {
        return providerhandler_enqueueMessage({
            type: 'exec',
            payload,
            singleRow,
        })
    },
    async enqueueRequest(req: WorkerRequest) {
        return providerhandler_enqueueMessage(req)
    },
}

export type ProviderHandlersProxy = Record<
    keyof typeof providerSQLHandlers,
    <T = any>(...args: any[]) => Promise<T>
>

// this function will be called by the shared worker if and only if this tab is promoted to provider
export const createProviderHandlers = () => createSharedServicePort(providerSQLHandlers)
